/**
 * Time formatters.
 *
 * CR-0013 (Δ-12): defensive `Z`-append guard.
 *
 * The server contract (C-19) is that every datetime is emitted as a
 * tz-aware ISO-8601 string with explicit offset (`+00:00` or `Z`).
 * If for any reason a naïve string ever reaches the client (older
 * persisted record, future regression, etc.), `new Date(string)` would
 * parse it as **local time** and introduce a phantom delta of exactly
 * the viewer's TZ offset. `parseServerInstant` defends against that by
 * appending `Z` when no offset designator is present.
 */
function parseServerInstant(iso) {
  if (!iso) return NaN;
  // ISO with timezone designator -> trust it.
  // Match `Z`, `+HH:MM`, `+HHMM`, `-HH:MM`, `-HHMM` after the time portion.
  if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(iso)) {
    return new Date(iso).getTime();
  }
  // Defensive: treat naïve ISO as UTC by appending `Z`.
  return new Date(`${iso}Z`).getTime();
}

export function formatElapsedMin(occupiedTime) {
  if (!occupiedTime) return null;
  const start = parseServerInstant(occupiedTime);
  const now = Date.now();
  // CR-0013a cosmetic floor: never show negative values caused by minor
  // server↔client clock skew. See CHANGELOG Δ-12a / discussion 2026-05-30.
  const minutes = Math.max(0, Math.floor((now - start) / 60000));
  return `${minutes} Min`;
}

export function formatWaiting(waitingStartTime) {
  if (!waitingStartTime) return null;
  const start = parseServerInstant(waitingStartTime);
  const now = Date.now();
  // CR-0013a cosmetic floor: see formatElapsedMin note.
  const totalSec = Math.max(0, Math.floor((now - start) / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min} Min ${sec}s`;
}

export function formatClockTime(isoString) {
  if (!isoString) return null;
  const ts = parseServerInstant(isoString);
  if (Number.isNaN(ts)) return null;
  const d = new Date(ts);
  let hour = d.getHours() % 12 || 12;
  const minute = d.getMinutes().toString().padStart(2, "0");
  const ampm = d.getHours() < 12 ? "AM" : "PM";
  return `${hour}:${minute} ${ampm}`;
}

/**
 * CR-0013 (Δ-12): the server emits `reservation_time` as an ISO
 * instant (C-20). Format it for display in the **viewer's** local
 * timezone — hostesses in IST see `"2:17 PM"`, not the server's
 * UTC clock as `"8:46 AM"`.
 */
export function formatReservationTime(isoString) {
  return formatClockTime(isoString);
}
